import { isRiskFree as protoIsRiskFree } from '@wkda/funnel-modules';

/**
 * @param {object} [carLeadData] - the react-query wrapper, not the payload
 * @returns {boolean} false only for an explicitly expired price
 */
export function hasValidPrice(carLeadData) {
  return carLeadData?.data?.priceExpiresInDays !== 0;
}

/**
 * Copy bucket for "valid for x days". `priceExpiresInDays` counts the last valid day itself
 * (1 = only today, 2 = one more day) — same rule as HandoverPriceCountdown.
 *
 * @param {number} priceExpiresInDays
 * @returns {'today'|'oneDay'|'days'}
 */
export function priceValidityCase(priceExpiresInDays) {
  if (priceExpiresInDays === 1) return 'today';
  if (priceExpiresInDays === 2) return 'oneDay';
  return 'days';
}

/**
 * Appointment statuses that mean the customer cancelled.
 */
const CANCELLED_APPOINTMENT_STATUS_IDS = [200, 300];

/**
 * @param {object} [carLeadData] - the react-query wrapper, not the payload
 * @returns {boolean} true unless `appointment` is explicitly null
 */
export function isAppointmentBooked(carLeadData) {
  return carLeadData?.data?.appointment !== null;
}

/**
 * @param {object} [carLeadData] - the react-query wrapper, not the payload
 * @returns {boolean}
 */
export function isAppointmentCancelled(carLeadData) {
  return CANCELLED_APPOINTMENT_STATUS_IDS.includes(carLeadData?.data?.statusId);
}

/**
 * @param {object} [carLeadData] - the react-query wrapper, not the payload
 * @returns {'valid'|'expired'|'reeva'}
 */
export function getPriceStatus(carLeadData) {
  if (hasValidPrice(carLeadData)) {
    return 'valid';
  }
  return protoIsRiskFree(carLeadData?.data) ? 'expired' : 'reeva';
}

/**
 * Derives the booking-page case for Carla's conversational prompts. An expired
 * price wins over a financed car, which wins over an ordinary valid price.
 *
 * Unlike `hasValidPrice`/`getPriceStatus` above, this takes the carlead payload
 * itself, not the react-query wrapper — don't pass it `{ data: carlead }`.
 *
 * `undefined !== 0`, so a missing lead resolves 'valid' — same fallback
 * direction as DEFAULT_PROFILE in prompts.js. `isFinanced` is `boolean | null`
 * on the self-evaluation, so only an explicit `true` counts.
 *
 * @param {object} [carlead] - the carlead payload (e.g. useGetConsumerCarlead().data)
 * @param {object} [segments]
 * @param {boolean} [segments.isFinanced] - the car still carries a loan
 * @returns {'valid'|'invalid'|'financed'}
 */
export function resolveBookingCase(carlead, {
  isFinanced
} = {}) {
  if (carlead?.priceExpiresInDays === 0) return 'invalid';
  if (isFinanced === true) return 'financed';
  return 'valid';
}